import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export interface AdminSkillCertificate {
  id: string;
  subject: string;
  topic: string;
  score: number;
  issued_at: string;
  verify_code: string;
  learner_user_id: string;
  learner_display_name: string | null;
}

export interface AdminSkillCertificateListResponse {
  items: AdminSkillCertificate[];
  total: number;
}

export interface AdminStudyGroup {
  id: string;
  name: string;
  description: string | null;
  pathway_id: string | null;
  subject: string | null;
  topic: string | null;
  member_count: number;
  max_members: number;
  invite_code: string;
  archived_at?: string | null;
}

export interface AdminStudyGroupListResponse {
  items: AdminStudyGroup[];
  total: number;
}

export interface AdminStudyGroupMessage {
  id: string;
  group_id: string;
  author_user_id: string;
  author_name: string;
  kind: string;
  body: string;
  is_deleted: boolean;
  deleted_by_name: string | null;
  created_at: string;
}

export interface AdminStudyGroupMessageListResponse {
  items: AdminStudyGroupMessage[];
  total: number;
}

export interface AdminStudyGroupBan {
  user_id: string;
  display_name: string;
}

export interface AdminStudyGroupBanListResponse {
  items: AdminStudyGroupBan[];
  total: number;
}

export interface AdminReadLog {
  id: string;
  pathway_id: string;
  subject: string;
  topic: string;
  minutes_spent: number | null;
  note: string | null;
  logged_at: string;
  learner_user_id: string;
  learner_display_name: string | null;
}

export interface AdminReadLogListResponse {
  items: AdminReadLog[];
  total: number;
}

export const learningAdminKeys = {
  all: ["learning-admin"] as const,
  certificates: (params?: { limit?: number; offset?: number }) =>
    [...learningAdminKeys.all, "certificates", params ?? {}] as const,
  studyGroups: (params?: { limit?: number; offset?: number }) =>
    [...learningAdminKeys.all, "study-groups", params ?? {}] as const,
  studyGroupMessages: (groupId: string | null, params?: { limit?: number; offset?: number }) =>
    [...learningAdminKeys.all, "study-group-messages", groupId, params ?? {}] as const,
  readLogs: (params?: { limit?: number; offset?: number }) =>
    [...learningAdminKeys.all, "read-logs", params ?? {}] as const,
  studyGroupBans: (groupId: string | null) =>
    [...learningAdminKeys.all, "study-group-bans", groupId] as const,
};

function buildQs(params?: { limit?: number; offset?: number }): string {
  const qs = new URLSearchParams();
  if (params?.limit != null) qs.set("limit", String(params.limit));
  if (params?.offset != null) qs.set("offset", String(params.offset));
  return qs.toString() ? `?${qs.toString()}` : "";
}

export function useAdminSkillCertificates(params?: { limit?: number; offset?: number }) {
  return useQuery<AdminSkillCertificateListResponse>({
    queryKey: learningAdminKeys.certificates(params),
    queryFn: () =>
      api.get<AdminSkillCertificateListResponse>(`/api/skill-certificates${buildQs(params)}`),
    staleTime: 15_000,
  });
}

export function useAdminStudyGroups(params?: { limit?: number; offset?: number }, enabled = true) {
  return useQuery<AdminStudyGroupListResponse>({
    queryKey: learningAdminKeys.studyGroups(params),
    queryFn: () => api.get<AdminStudyGroupListResponse>(`/api/study-groups${buildQs(params)}`),
    enabled,
    staleTime: 15_000,
  });
}

export function useAdminStudyGroupMessages(
  groupId: string | null,
  params?: { limit?: number; offset?: number },
) {
  return useQuery<AdminStudyGroupMessageListResponse>({
    queryKey: learningAdminKeys.studyGroupMessages(groupId, params),
    queryFn: () =>
      api.get<AdminStudyGroupMessageListResponse>(
        `/api/study-groups/${groupId}/messages${buildQs(params)}`,
      ),
    enabled: Boolean(groupId),
    staleTime: 10_000,
  });
}

export function useAdminReadLogs(params?: { limit?: number; offset?: number }) {
  return useQuery<AdminReadLogListResponse>({
    queryKey: learningAdminKeys.readLogs(params),
    queryFn: () => api.get<AdminReadLogListResponse>(`/api/read-logs${buildQs(params)}`),
    staleTime: 15_000,
  });
}

export function useDeleteStudyGroupMessage(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) =>
      api.delete(`/api/study-groups/${groupId}/messages/${messageId}`),
    onMutate: async (messageId: string) => {
      const prefix = [...learningAdminKeys.all, "study-group-messages", groupId] as const;
      await qc.cancelQueries({ queryKey: prefix });
      const snapshots = qc.getQueriesData<AdminStudyGroupMessageListResponse>({ queryKey: prefix });
      qc.setQueriesData<AdminStudyGroupMessageListResponse>({ queryKey: prefix }, (data) =>
        data
          ? {
              ...data,
              items: data.items.map((msg) =>
                msg.id === messageId ? { ...msg, is_deleted: true, body: "" } : msg,
              ),
            }
          : data,
      );
      return { snapshots };
    },
    onError: (_err, _messageId, context) => {
      for (const [key, data] of context?.snapshots ?? []) {
        qc.setQueryData(key, data);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({
        queryKey: [...learningAdminKeys.all, "study-group-messages", groupId],
      });
    },
  });
}

export function useSetStudyGroupArchived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ groupId, archived }: { groupId: string; archived: boolean }) =>
      api.post(`/api/study-groups/${groupId}/archive`, { archived }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroups() });
    },
  });
}

export function useBanStudyGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post(`/api/study-groups/${groupId}/ban`, { user_id: userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroups() });
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroupMessages(groupId) });
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroupBans(groupId) });
    },
  });
}

export function useAdminStudyGroupBans(groupId: string | null) {
  return useQuery<AdminStudyGroupBanListResponse>({
    queryKey: learningAdminKeys.studyGroupBans(groupId),
    queryFn: () => api.get<AdminStudyGroupBanListResponse>(`/api/study-groups/${groupId}/bans`),
    enabled: Boolean(groupId),
    staleTime: 10_000,
  });
}

export function useUnbanStudyGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post(`/api/study-groups/${groupId}/unban`, { user_id: userId }),
    onMutate: async (userId: string) => {
      const key = learningAdminKeys.studyGroupBans(groupId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<AdminStudyGroupBanListResponse>(key);
      if (previous) {
        const items = previous.items.filter((ban) => ban.user_id !== userId);
        qc.setQueryData<AdminStudyGroupBanListResponse>(key, { items, total: items.length });
      }
      return { previous };
    },
    onError: (_err, _userId, context) => {
      if (context?.previous) {
        qc.setQueryData(learningAdminKeys.studyGroupBans(groupId), context.previous);
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroupBans(groupId) });
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroups() });
    },
  });
}

export function useRemoveStudyGroupMember(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post(`/api/study-groups/${groupId}/remove-member`, { user_id: userId }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroups() });
      void qc.invalidateQueries({ queryKey: learningAdminKeys.studyGroupMessages(groupId) });
    },
  });
}
